import { useSelector } from 'react-redux'

import { useOrgContext } from 'context/OrgContext'
import {
    orgHasVipAgencyPermissions,
    userHasMarketAnalysisPermissions,
} from 'helpers/featurePermissions'
import { selectUserOrganizationFeaturePermissions } from 'selectors/auth'
import { RootReduxState, UserPermission } from 'types'

export interface GatedFeatures {
    buyBoxPerformance: boolean
    dspIntegration: boolean
    sellerCompliance: boolean
    sellingPartnerIntegration: boolean
    categoryPerformanceBenchmarking: boolean
    categoryPerformanceTabsEnabled: boolean
    executiveHomepage: boolean
    productDetailLinks: boolean
    setDashboardAsHomePage: boolean
    newSegmentExperience: boolean
    marketAnalysis: boolean
}

const TRIAL_ORG_ID = '8667'

export function useGatedFeatures(): GatedFeatures {
    const featurePermissions = useSelector<RootReduxState, UserPermission[]>(
        (state) => selectUserOrganizationFeaturePermissions(state)
    )
    const hasVipAgencyPermissions =
        orgHasVipAgencyPermissions(featurePermissions)
    const hasMarketAnalysisPermissions =
        userHasMarketAnalysisPermissions(featurePermissions)
    const { org, orgAccountStatus } = useOrgContext()

    const isStandAloneOrgOrVipAgency =
        hasVipAgencyPermissions ||
        (!org?.is_agency && !org?.agency && !org?.is_standalone_child_org)

    const integrations = orgAccountStatus?.integrations
    const hasSellerCentralIntegration = Boolean(
        integrations?.seller_central?.has_integration
    )
    const hasVendorCentralIntegration = Boolean(
        integrations?.vendor_central?.has_integration
    )
    const hasSPIntegration =
        hasSellerCentralIntegration || hasVendorCentralIntegration
    const hasDspIntegration = Boolean(
        integrations?.amazon_advertising_dsp?.has_integration
    )

    const canSeeExecutiveHomepage =
        isStandAloneOrgOrVipAgency && hasSPIntegration

    const isTrialsOrg =
        org?.id === TRIAL_ORG_ID || org?.agency?.id === TRIAL_ORG_ID

    const hasMarketAnalysisAccess = isTrialsOrg || hasMarketAnalysisPermissions

    return {
        buyBoxPerformance: isStandAloneOrgOrVipAgency,
        dspIntegration: hasDspIntegration,
        sellerCompliance: isStandAloneOrgOrVipAgency,
        sellingPartnerIntegration: hasSPIntegration,
        categoryPerformanceBenchmarking: isStandAloneOrgOrVipAgency,
        categoryPerformanceTabsEnabled: isStandAloneOrgOrVipAgency,
        executiveHomepage: canSeeExecutiveHomepage,
        productDetailLinks: isStandAloneOrgOrVipAgency,
        setDashboardAsHomePage: !canSeeExecutiveHomepage,
        newSegmentExperience: hasMarketAnalysisAccess,
        marketAnalysis: hasMarketAnalysisAccess,
    }
}
